import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { format } from "date-fns";
import { is } from "date-fns/locale";
import PageIntro from "../components/IntroBlock";
import BlockRenderer from "../components/BlockRenderer";
import NewsArticlePage from "../pages/NewsArticlePage";
import InvestmentsIndexPage from "../pages/InvestmentsIndexPage";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://127.0.0.1:8000";

function DynamicPage({ isHome = false }) {
  const params = useParams();
  const pageSlug = isHome ? "aldir" : (params.newsSlug || params.pageSlug || "aldir");

  const [data, setData] = useState(null);
  const [newsArticles, setNewsArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isDesktop, setIsDesktop] = useState(true);

  // Detect precise pointer (desktop-ish) vs coarse (touch)
  useEffect(() => {
    const mq = window.matchMedia("(pointer: fine)");
    setIsDesktop(mq.matches);
    const handleChange = (e) => setIsDesktop(e.matches);

    if (mq.addEventListener) {
      mq.addEventListener("change", handleChange);
    } else if (mq.addListener) {
      mq.addListener(handleChange);
    }

    return () => {
      if (mq.removeEventListener) {
        mq.removeEventListener("change", handleChange);
      } else if (mq.removeListener) {
        mq.removeListener(handleChange);
      }
    };
  }, []);

  // Fetch page data
  useEffect(() => {
    const fetchPageData = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.get(
          `${API_BASE_URL}/api/pages/?slug=${pageSlug}&full=true`
        );
        const pageData = response.data.items ? response.data.items[0] : response.data;

        if (pageData && pageData.id) {
          setData(pageData);
          setNewsArticles(pageData.news_articles || pageData.latest_news_articles || []);
        } else {
          setError("Page not found or empty response.");
        }
      } catch (err) {
        setError("Failed to load the page.");
      } finally {
        setLoading(false);
      }
    };

    fetchPageData();
  }, [pageSlug, isHome]);

  const pageType = data?.content_type
    ? data.content_type
        .replace(/^home\s*\|\s*/i, "")
        .replace(/\s*\|\s*/g, "-")
        .replace(/\s+/g, "-")
        .toLowerCase()
    : "";

  if (error) {
    return (
      <div className="text-center p-6 mt-120 vh-100">
        <h2>Oops! {error}</h2>
        <p>The page you are looking for may have been removed or is not published yet.</p>
        <a href="/">Go Back Home</a>
      </div>
    );
  }

  if (loading && !data) {
    return (
      <div className="text-center p-6 mt-120 vh-100">
        Loading…
      </div>
    );
  }

  if (!data) {
    return null;
  }

  // Route to special page types
  if (data.content_type?.toLowerCase().includes("news article page")) {
    return <NewsArticlePage data={data} />;
  }

  if (data.content_type?.toLowerCase().includes("investments index page")) {
    return (
      <div className="investments-index-page-parent">
        <PageIntro
          customTitle={data.custom_title}
          description={data.description}
          title={data.title}
          pageType={pageType}
          isDesktop={isDesktop}
        />
        <InvestmentsIndexPage data={data} />
      </div>
    );
  }

  if (data.content_type?.toLowerCase().includes("about us page")) {
    return (
      <div className={pageType}>
        <PageIntro
          customTitle={data.custom_title}
          description={data.description}
          title={data.title}
          pageType={pageType}
          isDesktop={isDesktop}
        />
        {Array.isArray(data.body) && (
          <BlockRenderer blocks={data.body} latest_news_articles={newsArticles} />
        )}
      </div>
    );
  }

  // Generic content pages
  return (
    <div
      className={`${pageType} ${
        pageType.includes("content-page")
          ? (isDesktop ? "container-xl" : "container-fluid")
          : ""
      }`}
    >
      <PageIntro
        customTitle={data.custom_title}
        description={data.description}
        title={data.title}
        pageType={pageType}
        isDesktop={isDesktop}
      />

      {pageSlug === "frettir" && newsArticles.length > 0 && (
        <div className="news-articles-block container-fluid">
          <div className="row justify-content-center">
            <div className="col-xl-8 col-md-10">
              <div className="news-container">
                {newsArticles.map((article) => (
                  <a
                    key={article.id}
                    href={article.page_ptr?.url || `/frettir/${article.slug}`}
                    className="d-flex news-card justify-content-between"
                  >
                    <div className="text-container">
                      <h3>{article.title}</h3>
                      <p
                        className="p-body-2"
                        dangerouslySetInnerHTML={{ __html: article.intro }}
                      />
                      <span className="date font-suisse">
                        {article.date
                          ? format(new Date(article.date), "d. MMMM yyyy", { locale: is })
                          : ""}
                      </span>
                    </div>
                    <div className="green-arrow-btn">
                      <svg
                        width="30"
                        height="22"
                        viewBox="0 0 30 22"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M1.66675 11H5.83341H1.66675ZM28.3334 11L18.3334 1L28.3334 11ZM28.3334 11L18.3334 21L28.3334 11ZM28.3334 11H10.8334H28.3334Z"
                          fill="#ffffff"
                        />
                        <path
                          d="M1.66675 11H5.83341M28.3334 11L18.3334 1M28.3334 11L18.3334 21M28.3334 11H10.8334"
                          stroke="#ffffff"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {Array.isArray(data.body) && (
        <BlockRenderer blocks={data.body} latest_news_articles={newsArticles} />
      )}
    </div>
  );
}

export default DynamicPage;
